import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { Paths } from "../Services/EndPoints";
import OptimizedImage from "./OptimizedImage";
import { createSlug, fetcher, formatLocation } from "../Utils/HelperFunctions";
import wcommerceMark from "../assets/images/wcommerce-mark.png";
import razorpayTrusted from "../assets/images/razorpay-trusted-business.svg";

/* ─── Static columns (routes fixed regardless of store) ──────────────────── */
// `heading` and `label` hold KEY NAMES, not copy -- LinkColumn resolves them
// through t() at render so a language switch re-reads them. Link labels that
// come from nav_drawer are store data and pass through untranslated, which is
// why LinkColumn falls back to the raw string when no such key exists.
const STORE_COLUMN = {
  heading: "footer.theStore",
  links: [
    { label: "footer.ourStory", href: "/about" },
    { label: "footer.knowYourCraft", href: "/journal" },
    { label: "footer.reviews", href: "/reviews" },
  ],
};
const POLICIES_COLUMN = {
  heading: "footer.policies",
  links: [
    { label: "footer.terms", href: "/policies/terms" },
    { label: "footer.privacy", href: "/policies/privacy" },
    { label: "footer.refunds", href: "/policies/returns" },
    { label: "footer.shipping", href: "/policies/shipping" },
    { label: "footer.cookieSettings", href: "/cookie-preferences" },
  ],
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function LinkColumn({ heading, links, onNavigate }) {
  const { t } = useTranslation();
  if (!links || links.length === 0) return null;
  return (
    // Links carry their own 24px-tall tap area (py-[5px] round an 11px/1.3 line), so the column
    // gap is gone; the title's bottom margin grows by 2px to keep the same title-to-link space.
    // The title is a label, not a heading (a <p>, so the page outline doesn't skip levels).
    <div className="flex flex-col">
      <p className="text-[9.5px] font-[500] tracking-[0.09em] uppercase text-white/60 mb-[6px]">
        {t(heading)}
      </p>
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href || "#"}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(link);
          }}
          className="inline-block py-[5px] text-[11px] text-white/90 no-underline leading-[1.3]"
        >
          {t(link.label)}
        </a>
      ))}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */

/**
 * StoreFooter
 *
 * Dark store footer, self-connected: store identity comes from Redux
 * `storeDetails` + the `about_me` endpoint, and the Shop/Craft/Jewelry link
 * columns are built from `nav_drawer` (same source & routing as NavDrawer, so
 * the footer always mirrors the real store). Every prop is an optional
 * override — pages that already pass store data keep working; pages that pass
 * nothing (Wishlist, 404, policies…) now show the real store instead of
 * placeholder copy.
 */
export default function StoreFooter({
  brandName: brandNameProp,
  ownerName: ownerNameProp,
  ownerFullName: ownerFullNameProp,
  storeLocation: storeLocationProp,
  contactAddress: contactAddressProp,
  gstin: gstinProp,
  year,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storeDetails = useSelector((s) => s.storeDetails);
  // Same SWR keys as NavDrawer → shared cache, no duplicate network cost.
  const { data: about } = useSWR(Paths.about_me, fetcher);
  const { data: nav } = useSWR(Paths.nav_drawer, fetcher);

  /* Identity — prop override ?? live data ?? placeholder */
  const brandName =
    brandNameProp ??
    storeDetails?.storeName ??
    about?.storeName ??
    "The Crafts Archive";
  const ownerName = ownerNameProp ?? about?.firstName;
  const ownerFullName =
    ownerFullNameProp ??
    ([about?.firstName, about?.lastName].filter(Boolean).join(" ") ||
      ownerName ||
      brandName);
  const storeLocation =
    storeLocationProp ?? formatLocation(about?.city, about?.state) ?? undefined;
  const contactAddress =
    contactAddressProp ??
    (storeLocation
      ? `${storeLocation}${about?.pinCode ? ` ${about.pinCode}` : ""}`
      : undefined);
  const gstin = gstinProp ?? about?.gstin ?? "";
  const copyrightYear = year || new Date().getFullYear();

  const subline = [
    ownerFullName ? t("footer.storeBy", { name: ownerFullName }) : null,
    storeLocation,
  ]
    .filter(Boolean)
    .join(" · ");

  /* Links from nav_drawer — mirrors NavDrawer routing */
  const goCategory = (catSlug, item) => {
    if (catSlug === "womens-fashion")
      sessionStorage.setItem("clickedHealthConcern", catSlug);
    else sessionStorage.removeItem("clickedHealthConcern");
    navigate(`/shop/${catSlug}/${createSlug(item.name)}/${item.id}`);
  };
  const toSubLinks = (list, catSlug) =>
    (list || []).map((it) => ({
      label: it.name,
      href: `/shop/${catSlug}/${createSlug(it.name)}/${it.id}`,
      onClick: () => goCategory(catSlug, it),
    }));
  const toCraftLinks = (list) =>
    (list || []).map((c) => ({
      label: c.name,
      href: `/craft/${createSlug(c.name)}/${c.id}`,
    }));

  const shopColumn = {
    heading: "footer.shop",
    links: [
      { label: "footer.verifiedHandloom", href: "/verified-handloom" },
      ...toSubLinks(nav?.shop, "womens-fashion"),
    ],
  };
  const clothingCraftColumn = {
    heading: "footer.shopClothingByCraft",
    links: toCraftLinks(nav?.shopClothingByCraft),
  };
  const jewelryColumn = {
    heading: "footer.jewelry",
    links: toSubLinks(nav?.jewelry, "jewelry"),
  };
  const jewelryCraftColumn = {
    heading: "footer.shopJewelryByCraft",
    links: toCraftLinks(nav?.shopJewelryByCraft),
  };

  const linkGroups = [
    [shopColumn, clothingCraftColumn],
    [jewelryColumn, jewelryCraftColumn],
    [STORE_COLUMN, POLICIES_COLUMN],
  ];

  const handleNavigate = (link) => {
    if (link.onClick) link.onClick();
    else if (link.href) navigate(link.href);
  };

  return (
    <footer className="bg-ink-900 text-white px-4 pt-[18px] pb-[22px] w-full">
      {/* Brand — the store's round logo beside the name, as on the store header */}
      <div className="flex items-center gap-[10px]">
        {storeDetails?.storeUrl && (
          <OptimizedImage
            src={storeDetails.storeUrl}
            alt=""
            className="h-10 w-10 object-cover flex-shrink-0 rounded-full"
          />
        )}
        <div className="min-w-0">
          <p className="text-[14px] font-[500]">{brandName}</p>
          {subline && (
            <p className="text-[10.5px] text-white/60 mt-[2px]">{subline}</p>
          )}
        </div>
      </div>

      {/* Link columns */}
      <div
        className="grid gap-x-[10px] gap-y-4 my-4 py-[15px] border-t border-white/[0.12] border-b border-white/[0.12]"
        style={{
          gridTemplateColumns: "auto auto auto",
          justifyContent: "space-between",
        }}
      >
        {linkGroups.map((group, gi) => (
          <div key={gi} className="flex flex-col gap-[15px]">
            {group.map((col, ci) => (
              <LinkColumn
                key={ci}
                heading={col.heading}
                links={col.links}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Contact */}
      <p className="text-[10.5px] text-white/70 leading-[1.65]">
        <b className="text-white">{ownerFullName}</b> · {brandName}
        {contactAddress && (
          <>
            <br />
            {contactAddress}
          </>
        )}
      </p>

      {/* GSTIN chip */}
      {gstin && (
        <div className="flex items-center gap-2 mt-[11px] text-[10.5px] text-white/[0.82]">
          {/* One-off brand green chip — kept as arbitrary since not in token set */}
          <span
            className="text-[8px] font-[500] uppercase tracking-[0.05em] px-[7px] py-[2px] rounded-pill"
            style={{
              color: "#9be8b6",
              background: "rgba(46,154,90,.18)",
              border: "1px solid rgba(46,154,90,.42)",
            }}
          >
            {t("footer.gstRegistered")}
          </span>
          GSTIN{" "}
          <b className="text-white font-[500] tracking-[0.02em]">{gstin}</b>
        </div>
      )}

      {/* Trust badges */}
      <div className="flex flex-wrap items-center gap-3 mt-[14px]">
        <span className="inline-flex items-center gap-[6px] text-[10px] text-white/75">
          <span className="w-[18px] h-[18px] rounded-[5px] bg-s-primary grid place-items-center overflow-hidden">
            <img
              src={wcommerceMark}
              alt="Wcommerce"
              className="w-full h-full block object-cover"
            />
          </span>
          {t("footer.fulfilledBy")}
        </span>
        <span className="inline-flex items-center bg-white rounded-[5px] py-[3px] px-[6px]">
          <img
            src={razorpayTrusted}
            alt="Razorpay Trusted Business"
            // The SVG's own size (viewBox 0 0 300 72): gives the browser the aspect ratio before it
            // loads, so its space is reserved. The classes still render it 17px tall (~71px wide).
            width={300}
            height={72}
            className="h-[17px] w-auto block"
          />
        </span>
      </div>

      {/* Copyright */}
      <p className="text-[9.5px] text-white/60 mt-3">
        {t("footer.copyright", { year: copyrightYear, brand: brandName })}
      </p>
    </footer>
  );
}
